import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Chip,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Switch,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { ManagerSelector } from '../../admin-console/Communications/Notifications/ManagerSelector';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textField: {
      marginBottom: '16px',
    },
    checkbox: {
      color: theme.palette.primary.main,
      '&.Mui-checked': {
        color: theme.palette.primary.main,
      },
    },
  })
);

const RECIPIENT_OPTIONS = [
  { field: 'notifyAdmins', label: 'All Admins' },
  { field: 'notifySpecificManagers', label: 'Specific Managers' },
  { field: 'notifyCustomEmail', label: 'Custom Email Addresses' },
];

interface NotificationConfigManager {
  managerId: string;
  profile: {
    preferredName: string;
    lastName: string;
    email: string | null;
  };
}

interface FormNotificationConfig {
  formNotificationConfigId: string;
  notifyAdmins: boolean;
  notifySpecificManagers: boolean;
  customEmail: string | null;
  isEnabled: boolean;
  managers: NotificationConfigManager[];
}

interface FormData {
  notifyAdmins: boolean;
  notifySpecificManagers: boolean;
  notifyCustomEmail: boolean;
  customEmail: string;
  selectedManagerIds: string[];
  isEnabled: boolean;
}

interface Props {
  formId: string;
  notificationConfig: FormNotificationConfig | null | undefined;
  onSaved?: () => void;
}

export const FormNotificationSettings = ({ formId, notificationConfig, onSaved }: Props) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const saveNotificationConfig = useCampfireFetch({ defer: true });

  const [formData, setFormData] = useState<FormData>({
    notifyAdmins: false,
    notifySpecificManagers: false,
    notifyCustomEmail: false,
    customEmail: '',
    selectedManagerIds: [],
    isEnabled: false,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setFormData({
      notifyAdmins: notificationConfig?.notifyAdmins ?? false,
      notifySpecificManagers: notificationConfig?.notifySpecificManagers ?? false,
      notifyCustomEmail: !!notificationConfig?.customEmail,
      customEmail: notificationConfig?.customEmail ?? '',
      selectedManagerIds: notificationConfig?.managers?.map((m) => m.managerId) ?? [],
      isEnabled: notificationConfig?.isEnabled ?? false,
    });
    setHasChanges(false);
    setErrors({});
  }, [notificationConfig]);

  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.isEnabled) {
      const hasRecipients = formData.notifyAdmins || formData.notifySpecificManagers || formData.notifyCustomEmail;

      if (!hasRecipients) {
        newErrors.recipients = 'At least one recipient type must be selected';
      }

      if (formData.notifySpecificManagers && formData.selectedManagerIds.length === 0) {
        newErrors.selectedManagerIds = 'At least one manager must be selected';
      }

      if (formData.notifyCustomEmail && !formData.customEmail.trim()) {
        newErrors.customEmail = 'Custom email addresses are required';
      }

      if (formData.notifyCustomEmail && formData.customEmail) {
        const emails = formData.customEmail
          .split(',')
          .map((email) => email.trim())
          .filter((email) => email);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (emails.length === 0) {
          newErrors.customEmail = 'At least one valid email address is required';
        } else {
          const invalidEmails = emails.filter((email) => !emailRegex.test(email));
          if (invalidEmails.length > 0) {
            newErrors.customEmail = `Invalid email address${invalidEmails.length > 1 ? 'es' : ''}: ${invalidEmails.join(
              ', '
            )}`;
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const res = await saveNotificationConfig.run({
        url: `${apiUrl}/vm/form/notification-config/save`,
        method: 'post',
        data: {
          formId,
          notifyAdmins: formData.notifyAdmins,
          notifySpecificManagers: formData.notifySpecificManagers,
          customEmails: formData.notifyCustomEmail
            ? formData.customEmail
                .split(',')
                .map((email) => email.trim())
                .filter((email) => email)
            : [],
          isEnabled: formData.isEnabled,
          managerIds: formData.notifySpecificManagers ? formData.selectedManagerIds : [],
        },
      });

      if (!res.ok) {
        setSnackbar({
          open: true,
          message: res.data?.message || 'Unable to save notification settings',
          variant: 'error',
        });
        return;
      }

      setSnackbar({
        open: true,
        message: 'Notification settings saved',
        variant: 'success',
      });
      setHasChanges(false);
      if (onSaved) onSaved();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Unable to save notification settings',
        variant: 'error',
      });
    }
  };

  return (
    <Box style={{ padding: '16px 0' }}>
      <Box display='flex' justifyContent='space-between' alignItems='center' style={{ marginBottom: 24 }}>
        <Box>
          <Typography style={{ fontSize: '14px', fontWeight: 500, color: theme.color.grey.neutral400 }}>
            Enable Notifications
          </Typography>
          <Typography style={{ fontSize: '13px', color: theme.color.grey.neutral300 }}>
            Send email notifications when this form receives a new submission
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={formData.isEnabled}
              onChange={(e) => handleFieldChange('isEnabled', e.target.checked)}
              color='primary'
              disabled={saveNotificationConfig.loading}
            />
          }
          label=''
        />
      </Box>

      <Collapse in={formData.isEnabled}>
        <Box>
          <Typography
            style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px', color: theme.color.grey.neutral400 }}
          >
            Recipients
          </Typography>
          <FormControl variant='outlined' fullWidth className={classes.textField} error={!!errors.recipients}>
            <InputLabel>Select Recipients</InputLabel>
            <Select
              multiple
              value={RECIPIENT_OPTIONS.filter((option) => formData[option.field as keyof FormData]).map(
                (option) => option.field
              )}
              onChange={(e) => {
                const selectedFields = e.target.value as string[];
                RECIPIENT_OPTIONS.forEach((option) => {
                  handleFieldChange(option.field as keyof FormData, selectedFields.includes(option.field));
                });
              }}
              input={<OutlinedInput label='Select Recipients' />}
              renderValue={(selected) => (
                <Box display='flex' flexWrap='wrap' style={{ gap: '4px' }}>
                  {(selected as string[]).map((field) => {
                    const option = RECIPIENT_OPTIONS.find((opt) => opt.field === field);
                    return <Chip key={field} label={option?.label} size='small' />;
                  })}
                </Box>
              )}
              disabled={saveNotificationConfig.loading}
            >
              {RECIPIENT_OPTIONS.map((option) => (
                <MenuItem key={option.field} value={option.field}>
                  <Checkbox
                    checked={formData[option.field as keyof FormData] as boolean}
                    className={classes.checkbox}
                  />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors.recipients && (
              <Typography variant='caption' color='error' style={{ display: 'block', marginTop: '4px' }}>
                {errors.recipients}
              </Typography>
            )}
          </FormControl>

          {formData.notifyCustomEmail && (
            <TextField
              fullWidth
              label='Custom Email Addresses'
              value={formData.customEmail}
              onChange={(e) => handleFieldChange('customEmail', e.target.value)}
              variant='outlined'
              className={classes.textField}
              disabled={saveNotificationConfig.loading}
              error={!!errors.customEmail}
              helperText={errors.customEmail || 'Separate multiple email addresses with commas'}
              placeholder='admin@example.com, manager@example.com'
              multiline
              rows={2}
            />
          )}

          {formData.notifySpecificManagers && (
            <Box>
              <Typography
                style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '12px',
                  color: theme.color.grey.neutral400,
                }}
              >
                Select Managers
              </Typography>
              <ManagerSelector
                selectedManagerIds={formData.selectedManagerIds}
                onChange={(managerIds) => handleFieldChange('selectedManagerIds', managerIds)}
                error={errors.selectedManagerIds}
                disabled={saveNotificationConfig.loading}
              />
            </Box>
          )}
        </Box>
      </Collapse>

      <Box display='flex' justifyContent='flex-end' style={{ marginTop: 24 }}>
        <TabletButton
          variant='contained'
          color='primary'
          onClick={handleSave}
          disabled={saveNotificationConfig.loading || !hasChanges}
        >
          {saveNotificationConfig.loading ? 'Saving...' : 'Save'}
        </TabletButton>
      </Box>
    </Box>
  );
};
